export type CoverageType = 'Specific' | 'Aggregate';

export type SubscriptionType = 'Composite' | 'Tiered by Subscription';

export type UnitType = 'Per Employee Per Month';

export type TieredSubscriptionType =
  | 'Employee'
  | 'Employee + Spouse'
  | 'Employee + Child(ren)'
  | 'Employee + Family';

export type FeeCalculationOption = 'Enter Breakdown' | 'Enter Total Fee';

export type FeeCalculationDetailsType = 'Monthly' | 'Quarterly' | 'Annually';

export interface FeeCalculationValue {
  dateRange: string;
  value: number | null;
}

export interface FeeCalculationDetail {
  type: string;
  values: FeeCalculationValue[];
}

export interface StoplossFeeCalculation {
  source?: string;
  option?: FeeCalculationOption;
  detailsType?: FeeCalculationDetailsType;
  details?: FeeCalculationDetail[];
}

export interface SubscriptionData {
  subscriptionID: string;
  type?: TieredSubscriptionType | 'Composite';
  premiumAmount?: number;
  unit?: UnitType;
  feeCalculation?: StoplossFeeCalculation;
  fees?: number;
}

export interface StoplossCoverage {
  coverage: CoverageType;
  subscriptionType?: SubscriptionType;
  subscription: SubscriptionData[];
}

export interface StopLossVendor {
  vendorID?: string;
  name: string;
  policyStartYear: string;
  policyEndYear: string;
  coverage: StoplossCoverage[];
}

export const emptyFeeCalculationValue = (): FeeCalculationValue => ({
  dateRange: '',
  value: 0,
});

export const emptyFeeCalculationDetail = (): FeeCalculationDetail => ({
  type: '',
  values: [emptyFeeCalculationValue()],
});

export const emptyStoplossFeeCalculation = (): StoplossFeeCalculation => ({
  source: '',
  option: undefined,
  detailsType: undefined,
  details: [],
});

export const emptySubscription = (): SubscriptionData => ({
  premiumAmount: 0,
  unit: 'Per Employee Per Month',
  fees: 0,
  feeCalculation: emptyStoplossFeeCalculation(),
  subscriptionID: '',
});

export const emptyTieredSubscriptions = (): SubscriptionData[] =>
  ([
    'Employee',
    'Employee + Spouse',
    'Employee + Child(ren)',
    'Employee + Family',
  ] as TieredSubscriptionType[]).map((type) => ({
    subscriptionID: '',
    type,
    premiumAmount: 0,
    unit: 'Per Employee Per Month' as UnitType,
    fees: 0,
    feeCalculation: emptyStoplossFeeCalculation(),
  }));

export const emptyCompositeSubscription = (): SubscriptionData[] => [
  {
    type: 'Composite',
    premiumAmount: 0,
    unit: 'Per Employee Per Month',
    fees: 0,
    feeCalculation: emptyStoplossFeeCalculation(),
    subscriptionID: '',
  },
];

export const emptyStoplossCoverage = (
  coverage: CoverageType,
  subscriptionType?: SubscriptionType,
): StoplossCoverage => {
  if (coverage === 'Aggregate') {
    return {
      coverage: 'Aggregate',
      subscription: [emptySubscription()],
    };
  }

  return {
    coverage: 'Specific',
    subscriptionType,
    subscription:
      subscriptionType === 'Tiered by Subscription'
        ? emptyTieredSubscriptions()
        : subscriptionType === 'Composite'
        ? emptyCompositeSubscription()
        : [],
  };
};

export const emptyStoplossVendor = (
  startDate?: string,
  endDate?: string,
): StopLossVendor => ({
  vendorID: '',
  name: '',
  policyStartYear: startDate ?? '',
  policyEndYear: endDate ?? '',
  coverage: [],
});
